import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useMediaQuery } from '../../components/data-table/useMediaQuery';
import { useMe } from '../auth/useMe';
import { fetchAvailability, fetchEstimate, type AssistantEstimate } from './assistant-api';

/** Below this width (the shell's phone frame, 1024 px) the assistant is a full-screen overlay instead of the side panel. */
const PHONE_QUERY = '(max-width: 1023px)';

export const ESTIMATE_KEY = ['assistant', 'estimate'] as const;
/** The availability probe; invalidating `['assistant']` refreshes it with the estimate. */
export const AVAILABILITY_KEY = ['assistant', 'availability'] as const;

interface AssistantState {
  /** The assistant is offered here: `content:read`, and the tenant has not switched it off. */
  readonly available: boolean;
  readonly open: boolean;
  /** The panel has been opened once, so it exists in the document (for `aria-controls`). */
  readonly opened: boolean;
  /** Below the phone breakpoint: the panel is a full-screen modal overlay. */
  readonly phone: boolean;
  readonly toggle: () => void;
  readonly close: () => void;
  /** Closes without moving focus to the header button, for when the member leaves the page (Oeffnen). */
  readonly dismiss: () => void;
  readonly buttonRef: RefObject<HTMLButtonElement | HTMLAnchorElement>;
  readonly canRun: boolean;
  readonly estimate: AssistantEstimate | undefined;
}

const AssistantContext = createContext<AssistantState | null>(null);

/**
 * The open state of the assistant panel and whether it is offered at all (spec 110 section 4.15.6).
 * Availability comes from `fetchAvailability` (the conversation list, guarded like the entry itself by
 * `content:read`): `403 assistant-disabled` hides the button for every role (AC-110-141). The credit
 * estimate is `ai:run` only and is asked for only by members who may run a turn. The header button and
 * the panel share this state, so closing returns focus to the button.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const phone = useMediaQuery(PHONE_QUERY);
  const me = useMe();
  const permissions = me.data?.active_tenant?.permissions;
  const canRead = permissions?.includes('content:read') ?? false;
  const canRun = permissions?.includes('ai:run') ?? false;
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const availability = useQuery({
    queryKey: AVAILABILITY_KEY,
    queryFn: fetchAvailability,
    enabled: canRead,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const available = canRead && availability.data !== false;
  const estimate = useQuery({
    queryKey: ESTIMATE_KEY,
    queryFn: fetchEstimate,
    enabled: available && canRun,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const toggle = useCallback(() => {
    setOpened(true);
    setOpen((current) => !current);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  const dismiss = useCallback(() => setOpen(false), []);

  const value = useMemo<AssistantState>(
    () => ({ available, open: available && open, opened, phone, toggle, close, dismiss, buttonRef, canRun, estimate: estimate.data }),
    [available, open, opened, phone, toggle, close, dismiss, canRun, estimate.data],
  );
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantState {
  const value = useContext(AssistantContext);
  if (!value) throw new Error('useAssistant needs an AssistantProvider');
  return value;
}
